import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetCustomersReport } from '../../../hooks/reports/queries';
import { exportCustomersReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState, MRT_SortingState } from 'mantine-react-table';
import { getRangeDate } from '../datePickerHelper';

export interface CustomersReportTableInfoData {
  lastOrderDate: string | null;
  ordersQty: number;
  ordersSalesTotal: number;
  ordersUnpaidTotal: number;
  _id: {
    createdDate: string;
    email: string;
    name: string;
    lastName: string;
    phone: string;
    _id: string;
  };
}
const CustomersReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const { fromDate, toDate } = getRangeDate(dateRangePicker);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'lastOrderDate', desc: true }
  ]);
  const activeSort = sorting[0];
  const sortBy = activeSort?.id === 'ordersQty' || activeSort?.id === 'ordersSalesTotal'
    ? activeSort.id
    : 'lastOrderDate';
  const sortOrder = activeSort?.desc === false ? 'asc' : 'desc';
  const handleSortingChange: typeof setSorting = (updater) => {
    setPagination((current) => current.pageIndex === 0
      ? current
      : { ...current, pageIndex: 0 });
    setSorting(updater);
  };
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetCustomersReport(searchParamStoreId, {
    fromDate,
    toDate,
    sortBy,
    sortOrder
  });

  const tableInfoData: CustomersReportTableInfoData[] = useMemo(() => {
    return data?.tableInfo;
  }, [data]);
  const totalRowsCount = useMemo(() => data?.totalRows, [data]);
  const pages = useMemo(
    () => Math.ceil(totalRowsCount / pagination.pageSize) || -1,
    [pagination.pageSize, totalRowsCount]
  );

  return (
    <CatalogContainer>
      <CatalogListHeader title={t('ReportsPage:customersReport.title')} showCreateButton={false} />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportCustomersReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      sortBy,
                      sortOrder
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={handleSortingChange}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default CustomersReport;
